import React, { useState, useEffect } from "react";
import { useHeaderContext } from "../../../context/headerContext";
import { useCartContext } from "../../../context/cartContext";
import { useNavigate } from "react-router";
import { useDrawerContext } from "../../../context/drawerContext";
import ProductSummaryCard from "../../../components/Shop/ProductSummaryCard";
import ShopDropdown from "../../../components/Shop/ShopDropdown";
import TextInput from "../../../components/TextInput";
import Button from "../../../components/Button";
import { useLocation } from "react-router-dom";
import { z } from 'zod';
import generateUUID from "../../../utils/generateUUID";
import { useUserContext } from "../../../context/userContext";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";

import CloseIcon from "/public/images/icons/close.svg?react";
import { parse, set } from "date-fns";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function ProductSummary() {
  const [errors, setErrors] = useState({});
  const [toggleChecked, setToggleChecked] = useState({});
  const [activeDropdowns, setActiveDropdowns] = useState({});
  const [formData, setFormData] = useState({});

  const { setTitle, setBackButton, setCartButton, setCartButtonFunc } = useHeaderContext();
  const { participants, selectedParticipant, updateParticipantDetails, products, cart } = useCartContext();
  const navigate = useNavigate();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const { user, logout, updateTokens } = useUserContext();
  
  const location = useLocation();
  const { edit } = location.state;

  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get('productId');
  const participantId = queryParams.get('participantId'); 

  const [product, setProduct] = useState(null);
  const [foundParticipant, setFoundParticipant] = useState(null);

  useEffect(() => {
    if (edit && product?.CartLineItemId) {
      fetchProduct();
    }
  }, [product?.CartLineItemId]);

  useEffect(() => {
    let foundProduct;
    if (edit ? product?.productId : productId) {
      foundProduct = products.find((product) => product.ProductId === productId);
    } else {
      foundProduct = cart?.cartItems?.find((product) => product.CartLineItemId === productId && product.ParticipantId === participantId);
    }
    if (foundProduct) {
      setProduct(foundProduct);
    } else {
      navigate('/shop/my-participants');
    }
  }, [productId, products, cart.cartItems, participantId]);


  useEffect(() => {
    setTitle("PRODUCT SUMMARY");
    setBackButton(true);
    setCartButton(true);

    const handleBack = () => {
      navigate('/cart');
    };

    setCartButtonFunc(handleBack);

    // Initialize form data for index 0 from selectedParticipant
    if (selectedParticipant) {
      setFormData({
        0: { ...selectedParticipant, ParticipantId: selectedParticipant.ParticipantId || generateUUID() }
      });
    }
  }, [selectedParticipant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
  
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
  
    const parsedData = {
      ParticipantName: [],
      IceNumber: [],
      PrimaryMedicalCondition: [],
      DetailsMatch: []
    };
  
    Array.from({ length: product.Quantity }).forEach((_, index) => {
      parsedData.ParticipantName[index] = data[`ParticipantName-${index}`] || '';
      parsedData.IceNumber[index] = data[`IceNumber-${index}`] || '';
      parsedData.PrimaryMedicalCondition[index] = data[`PrimaryMedicalCondition-${index}`] || '';
      parsedData.DetailsMatch[index] = data[`DetailsMatch-${index}Value`] === 'on' ? true : false;
    });
  
    const participantSchema = z.object({
      ParticipantName: z.string()
      .trim()
      .min(1, "Participant Name is required")
      .max(product?.prnamemax ?? 50, `Participant Name must be at most ${product?.prnamemax ?? 50} characters`),
      IceNumber: z.string()
      .trim()
      .min(1, "ICE Contact telephone number is required")
      .max(product?.pricemax ?? 20, `Ice Number must be at most ${product?.pricemax ?? 20} characters`),
      PrimaryMedicalCondition: z.string()
      .trim()
      .min(1, "Primary Medical Condition is required")
      .max(product?.prmedicalmax ?? 100, `Primary Medical Condition must be at most ${product?.prmedicalmax ?? 100} characters`),
    });
  
    const chunkedData = Object.keys(parsedData.ParticipantName).map((key, index) => ({
      CartLineItemEngravingId: edit ? product?.cartLineItemsEngravings[index]?.CartLineItemEngravingId : null,
      ParticipantName: parsedData.DetailsMatch[index] ? parsedData.ParticipantName[0] : parsedData.ParticipantName[index],
      IceNumber: parsedData.DetailsMatch[index] ? parsedData.IceNumber[0] : parsedData.IceNumber[index],
      PrimaryMedicalCondition: parsedData.DetailsMatch[index] ? parsedData.PrimaryMedicalCondition[0] : parsedData.PrimaryMedicalCondition[index],
      DetailsMatch: parsedData.DetailsMatch[index] ? 1 : 0
    }));
  
    chunkedData.forEach((chunk, index) => {
      try {
        participantSchema.parse(chunk);
      } catch (error) {
        LogErrors(user, logout, updateTokens, error);
        const newErrors = {};
        error.errors.forEach((err) => {
          newErrors[`${err.path[0]}-${index}`] = err.message;
        });
        setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
        throw new Error("Validation failed");
      }
    });
  
    updateParticipantDetails(productId, participantId, chunkedData);
    await addToCart(chunkedData);
    navigate('/cart');
  };
  

  const removeCartItem = async () => {
    try {
      return await Sentry.startSpan({ name: "Remove Cart Item", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/CartParticipant?cartLineItemId=${product.CartLineItemId}`;
        span.setAttribute("http.url", url);
        span.setAttribute("cartLineItemId", product.CartLineItemId);
        span.setAttribute("userId", user?.id);
  
        const response = await fetchWithRefresh(
          url,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (!response.ok) {
          throw new Error(`Failed to remove cart item. Status: ${response.status}`);
        }
  
        span.setStatus("ok");
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { cartLineItemId: product.CartLineItemId, userId: user?.id } });
      LogErrors(user, logout, updateTokens, "Error removing cart item: " + error);
    }
  };  

  const clearBasketConfirmationDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            REMOVE BAND
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Remove this band and its variants from your shopping cart?</h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            All progress will be lost and this item, along with it&apos;s multiple band variants will be removed from your shopping cart.
          </p>
          <div>
            <Button 
              variant="primary" 
              className="w-full mt-4"
              onClick={() => {removeCartItem(), setOpen(false), navigate('/cart')}}
            >
              Confirm & Continue
            </Button>
            <Button
              variant="quaternary"
              theme=""
              className="w-full mt-4"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const confirmEngravingChangesDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            CHANGE ENGRAVING
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Are you sure you want to change the engraving?</h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            If you change information in these fields it means your new band will have different information to your profile.
          </p>
          <div>
            <Button 
              variant="primary" 
              className="w-full mt-4"
              onClick={() => {setOpen(false)}}
            >
              Continue
            </Button>

          </div>
        </div>
      </div>
    )
  }
  
  const handleToggleChange = (e, index) => {
    setToggleChecked((prev) => ({
      ...prev,
      [index]: e.target.checked,
    }));
    setActiveDropdowns((prev) => ({
      ...prev,
      [index]: !e.target.checked,
    }));
  };

  const addToCart = async (chunkedData) => {
    try {
      return await Sentry.startSpan({ name: "Add to Cart", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/CartParticipant`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
        span.setAttribute("productId", product?.BandVenueId || product?.OrgID);
        span.setAttribute("quantity", product?.Quantity);
  
        const cartItems = await fetchWithRefresh(
          url,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
            body: JSON.stringify(edit
              ? {
                  "userAccountId": user.id,
                  "bandVenueId": product.BandVenueId,
                  "bandVenueColour": product.BandVenueColour,
                  "quantity": product.Quantity,
                  "cartLineItemParticipantPending": product.ParticipantPending,
                  "participantId": product.ParticipantId.includes('new-participant') ? '' : product.ParticipantId,
                  "CartLineItemId": productId,
                  "cartLineItemsEngravings": chunkedData?.map((engraving) => ({
                    "cartLineItemEngravingId": engraving.CartLineItemEngravingId,
                    "cartLineItemParticipantName": engraving.ParticipantName,
                    "cartLineItemParticipantICE": engraving.IceNumber,
                    "cartLineItemPrimaryMedical": engraving.PrimaryMedicalCondition,
                    "cartLineItemDetailsMatchBand1": engraving.DetailsMatch
                  }))
                }
              : {
                  "userAccountId": user.id,
                  "bandVenueId": product.OrgID,
                  "bandVenueColour": product.BandColour,
                  "quantity": product.Quantity,
                  "cartLineItemParticipantPending": product.ParticipantId.includes('new-participant') ? 1 : 0,
                  "participantId": product.ParticipantId.includes('new-participant') ? '' : product.ParticipantId,
                  "cartLineItemsEngravings": chunkedData?.map((engraving) => ({
                    "cartLineItemParticipantName": engraving.ParticipantName,
                    "cartLineItemParticipantICE": engraving.IceNumber,
                    "cartLineItemPrimaryMedical": engraving.PrimaryMedicalCondition,
                    "cartLineItemDetailsMatchBand1": 0
                  }))
                })
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (cartItems.ok) {
          // LogErrors(user, logout, updateTokens, "Cart items added successfully");
          span.setStatus("ok");
        } else {
          throw new Error(`Failed to add items to cart. Status: ${cartItems.status}`);
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id, productId: product?.BandVenueId || product?.OrgID } });
      LogErrors(user, logout, updateTokens, "Error adding to cart: " + error);
    }
  };

  const fetchProduct = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Product", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/CartParticipant?cartLineItemId=${product?.CartLineItemId}`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
        span.setAttribute("cartLineItemId", product?.CartLineItemId);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (response.ok) {
          const data = await response.json();
          setProduct({ 
            ...product, 
            prnamemax: data[0].prnamemax,
            pricemax: data[0].pricemax,
            prmedicalmax: data[0].prmedicalmax,
            cartLineItemsEngravings: data[0].cartLineItemsEngravings 
          });
          span.setStatus("ok");
        } else {
          navigate('/shop/my-participants');
          throw new Error("Failed to fetch product details.");
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id, cartLineItemId: product?.CartLineItemId } });
      LogErrors(user, logout, updateTokens, "Error fetching product: " + error);
    }
  };

  const [hasFocused, setHasFocused] = useState(false);

//  const ParticipantInput = ({ index, participant, foundParticipant, errors, edit }) => {

  const handleFirstFocus = () => {
    if (!hasFocused) {
      setHasFocused(true);
      
      confirmEngravingChangesDrawer();

    }
  };


  useEffect(() => {
    if (product?.cartLineItemsEngravings) {
      const initialToggleChecked = product.cartLineItemsEngravings.reduce((acc, engraving, index) => {
      acc[index] = engraving.CartLineItemDetailsMatchBand1 === 1;
      return acc;
      }, {});
      setToggleChecked(initialToggleChecked);
    } else {
      const initialToggleChecked = Array.from({ length: product?.Quantity }).reduce((acc, _, index) => {
      acc[index] = true;
      return acc;
      }, {});
      setToggleChecked(initialToggleChecked);
    }
  }, [product?.cartLineItemsEngravings, product?.Quantity]);
  
  return (
    <div className="p-[20px]">
      <div className="mb-[16px]">
        <ProductSummaryCard product={product} removeProducts={() => clearBasketConfirmationDrawer(product)} />
      </div>
      <form id="bandDetailsForm" onSubmit={handleSubmit} className="flex flex-col gap-[17px] pb-[100px]">
          {Array.from({ length: product?.Quantity }).map((_, index) => {
            const isToggleChecked = toggleChecked[index];
            
            // Find the participant with the selectedParticipant ID
            const participant = edit ? product?.cartLineItemsEngravings && product?.cartLineItemsEngravings[index] : participants.find((participant) => participant.ParticipantId === selectedParticipant);
            const hasError = Object.keys(errors).some((key) => key.includes(`-${index}`));
            
            // LogErrors(user, logout, updateTokens, !toggleChecked[index])
            // LogErrors(user, logout, updateTokens, index, index === 0)
            return (
              <ShopDropdown 
                key={index} 
                title={`Engraving Details ${index + 1}`} 
                showSlider={index !== 0 && product?.Quantity > 1} 
                toggleChecked={isToggleChecked} 
                handleToggleChange={(e) => handleToggleChange(e, index)}
                active={index === 0 ? true : activeDropdowns[index] || !toggleChecked[index] }
                error={hasError}
                setActive={(value) => setActiveDropdowns((prev) => ({ ...prev, [index]: value }))}
                index={index}
              >
              <div className='flex flex-col gap-5'>
                {(!toggleChecked[index] || index === 0 || activeDropdowns[index]) && (
                <>
                  <TextInput 
                    name={`ParticipantName-${index}`} 
                    label="Participant Name" 
                    placeholder="Enter full name" 
                    error={errors[`ParticipantName-${index}`]}
                    defaultValue={edit ? participant?.CartLineItemParticipantName : foundParticipant?.ParticipantName || (index === 0 && participant) ? participant.ParticipantName : ''}
                    onFocus={handleFirstFocus} // Fires ONLY on first tap
                  />
                  <TextInput 
                    type="tel" 
                    name={`IceNumber-${index}`} 
                    label="ICE Contact Telephone Number" 
                    placeholder="Telephone Number" 
                    error={errors[`IceNumber-${index}`]}
                    defaultValue={edit ? participant?.CartLineItemParticipantICE : foundParticipant?.PrimaryIceNumber || (index === 0 && participant) ? participant.PrimaryIceNumber || participant.IceNumber : ''}
                    onFocus={handleFirstFocus} // Fires ONLY on first tap
                  />
                  <TextInput 
                    name={`PrimaryMedicalCondition-${index}`} 
                    label="Primary Medical Condition" 
                    placeholder="Enter Condition" 
                    error={errors[`PrimaryMedicalCondition-${index}`]} 
                    defaultValue={edit ? participant?.CartLineItemPrimaryMedical : foundParticipant?.PrimaryMedicalCondition || (index === 0 && participant) ? participant.PrimaryMedicalCondition : ''}
                    onFocus={handleFirstFocus} // Fires ONLY on first tap
                  />
                </>
                )}
              </div>
              </ShopDropdown>
            );
          })}
        {Object.keys(errors).length > 0 && (<div className="text-sportsRed text-center w-full">Please fix the errors above before submitting</div>)}
        <div className="border-t border-dashed border-[#E3E3E3] mt-[120px] pt-[21px]">
          <Button variant="primary" className="w-full" submit form="ba</div>ndDetailsForm">Confirm & Continue</Button>
        </div>
      </form>
    </div>
  );
}